// 卓作成ウィザードの formState を TableApi.createTable に渡すペイロードへ変換する。
// 既存 TableCreateDrawer.vue のロジック (addVendingHistoryTemp + buildVendingHistoriesToSubmit) と
// 同形のオブジェクトを生成する。
//
// 入力: formState（TableCreateWizard.vue で管理）
// 出力: { hallId, tableNumber, customerCount, customerIds, customerTableTaggings,
//        referringHistories, vendingHistories }

const buildVendingHistoryValue = ({
  vendibleId,
  vendibleType,
  sellingPrice,
  vendingHistoryCustomersAttributes = [],
  vendibleRecipientsAttributes = [],
  taxCharge = 'normal',
  persistenceReportAttributes = null,
}) => ({
  id: null,
  vendibleId,
  vendibleType,
  recipientId: null,
  taxCharge,
  isNonExistVendible: false,
  vendibleName: null,
  sellingPrice,
  vendingContributionsAttributes: [],
  vendibleRecipientsAttributes,
  vendingHistoryCustomersAttributes,
  ...(persistenceReportAttributes ? { persistenceReportAttributes } : {}),
  _destroy: null,
  isPaidInAdvance: false,
})

// Step1 で選択した Course を Step4 の set state に基づいて vendingHistory の配列に展開
// taxCharge: 'normal' | 'cut'（'cut' は売上から除外する税扱い、既存仕様と同じ）
// taxCharge は formState.taxCharge（トップ）を優先、後方互換で set.taxCharge をフォールバック
// コースの実利用時間（秒）。effective_duration_seconds 優先で旧フィールドにもフォールバック
const courseDurationSeconds = course => {
  if (!course) return null
  if (course.effectiveDurationSeconds != null) return Number(course.effectiveDurationSeconds)
  if (course.servingTimeMin != null) return Number(course.servingTimeMin) * 60
  if (course.durationMin != null) return Number(course.durationMin) * 60

  return null
}

// startAt + 持続秒数 = releaseAt (Date) 。秒数が無い場合は null（制限時間なし）
const computeReleaseAt = (startDate, durationSec) => {
  if (!(startDate instanceof Date)) return null
  if (durationSec == null) return null

  return new Date(startDate.getTime() + durationSec * 1000)
}

// クライアント側で persistence_report_attributes を組む。release_at が未指定だと
// model の build_new_persistence_reports をスキップする副作用で release_at = nil となるため、
// duration 由来の release_at もここで計算して付与する。
const buildPersistenceReportAttributes = (startVal, course) => {
  let start = null
  if (startVal instanceof Date) start = startVal
  else if (startVal) start = new Date(startVal)
  if (!start || Number.isNaN(start.getTime())) return null
  const releaseAt = computeReleaseAt(start, courseDurationSeconds(course))

  return {
    startAt: start.toISOString(),
    ...(releaseAt ? { releaseAt: releaseAt.toISOString() } : {}),
  }
}

const buildCourseVendingHistories = ({
  selectedCourse, set, customers, taxCharge, customerCount, startAt,
}) => {
  if (!selectedCourse) return []

  const customerAttrs = customers
    .filter(c => c.customerId)
    .map(c => ({ customerId: c.customerId }))

  const bulkTaxCharge = taxCharge || set.taxCharge || 'normal'

  if (set.mode === 'individual') {
    return (set.perCustomer || []).map(row => {
      const rowStart = row.startAt || startAt

      return buildVendingHistoryValue({
        vendibleId: selectedCourse.id,
        vendibleType: 'Course',
        sellingPrice: row.price != null ? row.price : selectedCourse.sellingPrice,
        taxCharge: row.taxCharge || bulkTaxCharge,
        vendingHistoryCustomersAttributes: customerAttrs,
        persistenceReportAttributes: buildPersistenceReportAttributes(rowStart, selectedCourse),
      })
    })
  }

  // bulk: customerCount 個複製（顧客未紐付け時も客数に従う）
  const count = Math.max(Number(customerCount) || 0, customers.length, 1)
  const bulkPersistence = buildPersistenceReportAttributes(startAt, selectedCourse)
  const result = []
  for (let i = 0; i < count; i += 1) {
    result.push(buildVendingHistoryValue({
      vendibleId: selectedCourse.id,
      vendibleType: 'Course',
      sellingPrice: set.price || selectedCourse.sellingPrice,
      taxCharge: bulkTaxCharge,
      vendingHistoryCustomersAttributes: customerAttrs,
      persistenceReportAttributes: bulkPersistence,
    }))
  }

  return result
}

// 各お客様の nominations を vendingHistory 配列に展開
// 各 nomination はアイテム個別に taxCharge を持つ（未指定なら 'normal'）
const buildNominationVendingHistories = ({ customers }) => customers.flatMap(customer => (
  (customer.nominations || []).map(nom => buildVendingHistoryValue({
    vendibleId: nom.vendibleId,
    vendibleType: 'Nomination',
    sellingPrice: nom.sellingPrice,
    taxCharge: nom.taxCharge || 'normal',
    vendingHistoryCustomersAttributes: customer.customerId
      ? [{ customerId: customer.customerId }]
      : [],
    vendibleRecipientsAttributes: nom.castId ? [{ userId: nom.castId }] : [],
  }))
))

// チャージ (Items) を vendingHistory 配列に展開
// 各 charge はアイテム個別に taxCharge を持つ（未指定なら 'normal'）
const buildChargeVendingHistories = ({ charges, customers }) => {
  const customerAttrs = customers
    .filter(c => c.customerId)
    .map(c => ({ customerId: c.customerId }))

  return (charges || [])
    .filter(c => c.enabled === true)
    .map(c => buildVendingHistoryValue({
      vendibleId: c.vendibleId,
      vendibleType: 'Item',
      sellingPrice: c.sellingPrice,
      taxCharge: c.taxCharge || 'normal',
      vendingHistoryCustomersAttributes: customerAttrs,
    }))
}

// リファラル: 各お客様の referrals をフラット化
const buildReferringHistories = ({ customers }) => customers.flatMap(customer => (
  (customer.referrals || [])
    .filter(r => r.referralId)
    .map(r => ({
      referralId: r.referralId,
      userId: r.userId || null,
    }))
))

// VendingContributionTrigger を {triggerableType, triggerableId} に正規化
const normalizeTriggers = (triggers = []) => (triggers || []).map(t => {
  const attrs = t?.attributes || t || {}

  return {
    triggerableType: attrs.triggerableType,
    triggerableId: Number(attrs.triggerableId),
  }
}).filter(t => t.triggerableType && Number.isFinite(t.triggerableId))

// 指名・リファラルとトリガを突合し、売上計上先となる userId 集合を返す
const pickContributorUserIds = ({ customers, triggers }) => {
  const normalized = normalizeTriggers(triggers)
  if (normalized.length === 0) return []

  const matches = (type, id) => normalized.some(t => (
    t.triggerableType === type && t.triggerableId === Number(id)
  ))

  const ids = new Set()
  customers.forEach(customer => {
    (customer.nominations || []).forEach(nom => {
      if (nom.castId && matches('Nomination', nom.vendibleId)) {
        ids.add(Number(nom.castId))
      }
    });
    (customer.referrals || []).forEach(ref => {
      if (ref.userId && matches('Referral', ref.referralId)) {
        ids.add(Number(ref.userId))
      }
    })
  })

  return Array.from(ids)
}

// 全 vendingHistory に等分の vendingContributionsAttributes を割り当てる
const applyVendingContributions = (vendingHistories, contributorIds) => {
  if (!Array.isArray(vendingHistories) || vendingHistories.length === 0) return vendingHistories
  if (!contributorIds || contributorIds.length === 0) return vendingHistories

  const ratio = 1 / contributorIds.length
  const attrs = contributorIds.map(userId => ({ userId, ratio }))

  return vendingHistories.map(vh => ({
    ...vh,
    vendingContributionsAttributes: attrs.map(a => ({ ...a })),
  }))
}

export const buildTableCreatePayload = ({
  hallId,
  tableNumber,
  formState,
  selectedCourse,
  startAt,
  vendingContributionTriggers,
  // シングルチャージの上書き指定 { chargeSettingId, rate, applied }。null なら送らない(サーバ側自動適用)
  tableCharge = null,
}) => {
  const customers = formState.customers || []
  const contributorIds = pickContributorUserIds({
    customers,
    triggers: vendingContributionTriggers,
  })

  const vendingHistories = [
    ...buildCourseVendingHistories({
      selectedCourse,
      set: formState.set,
      customers,
      taxCharge: formState.taxCharge,
      customerCount: formState.customerCount,
      startAt,
    }),
    ...buildNominationVendingHistories({ customers }),
    ...buildChargeVendingHistories({ charges: formState.charges, customers }),
  ]

  return {
    hallId,
    tableNumber,
    startAt: startAt instanceof Date ? startAt.toISOString() : startAt || null,
    customerCount: formState.customerCount || customers.length || 1,
    customerIds: customers.map(c => c.customerId).filter(Boolean),
    // CustomerTableTagging の updated イベントが返す形式をそのまま送る（旧 TableCreateDrawer と同じ）
    customerTableTaggings: (formState.customerTableTaggings || []).filter(t => !t._destroy),
    referringHistories: buildReferringHistories({ customers }),
    vendingHistories: applyVendingContributions(vendingHistories, contributorIds),
    ...(tableCharge ? { tableCharge } : {}),
  }
}

export default () => ({ buildTableCreatePayload })
